import { useEffect } from "react";

/**
 * Locks page scroll while `isLocked` is true (e.g. while a mobile menu is open).
 *
 * Plain `document.body.style.overflow = "hidden"` is enough on most desktop
 * browsers but is well known to leak touch-scroll on iOS Safari, and can be
 * silently overridden if any stylesheet sets `overflow` on html/body with
 * `!important` (inline JS styles lose to !important rules in a stylesheet).
 *
 * To stay robust against both issues, this hook:
 *   1. Injects a single <style> tag with `!important` overflow:hidden rules
 *      on html/body, guaranteed to beat any existing !important rule because
 *      it's appended last in the cascade (later same-specificity rules win).
 *   2. Also applies position:fixed + negative top offset on body, the
 *      standard fix for iOS Safari's touch-scroll-behind-fixed-overlay bug.
 *
 * Usage:
 *   const [menuOpen, setMenuOpen] = useState(false);
 *   useBodyScrollLock(menuOpen);
 */
const LOCK_STYLE_ID = "body-scroll-lock-style";

export default function useBodyScrollLock(isLocked) {
  useEffect(() => {
    if (!isLocked) return;

    const scrollY = window.scrollY;
    const { body } = document;

    // 1. Inject an !important override that beats any existing
    //    `overflow: visible !important` rule on html/body.
    const styleTag = document.createElement("style");
    styleTag.id = LOCK_STYLE_ID;
    styleTag.textContent = `
      html, body {
        overflow: hidden !important;
        height: 100% !important;
      }
    `;
    document.head.appendChild(styleTag);

    // 2. iOS Safari fallback: pin body in place so touch-scroll behind a
    //    fixed-position overlay can't move the page.
    const previousStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      styleTag.remove();
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.width = previousStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, [isLocked]);
}